/**
 * ResearchPanel — the tech tree + team hires.
 */

import { toast } from '@/hooks/useToast';
import { CheckCircle2, Lock, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PanelSection, Meter } from '@/components/game/ui';
import { useGameStore } from '@/store/gameStore';
import { money, formatGameDate } from '@/game/format';
import { RESEARCH_TREE } from '@/game/constants';
import { has, isUnlocked, researchDays } from '@/game/research';

const TIER_LABELS = { 1: 'Tier 1 — Survive', 2: 'Tier 2 — Differentiate', 3: 'Tier 3 — Scale', 4: 'Tier 4 — Empire' };

export function ResearchPanel() {
  const state = useGameStore((s) => s.state)!;
  const startResearch = useGameStore((s) => s.startResearch);
  const hire = useGameStore((s) => s.hire);

  const inProg = state.research.inProgress;

  return (
    <div className="space-y-6">
      {/* In progress */}
      {inProg && (
        <Card>
          <CardContent className="py-4">
            {(() => {
              const node = RESEARCH_TREE.find((n) => n.id === inProg.nodeId);
              const total = node ? researchDays(state, node.rDays) : 1;
              const done = state.time.day - (inProg.completeDay - total);
              return (
                <Meter
                  label={`R&D in progress: ${node?.name ?? '???'}`}
                  value={(done / Math.max(1, total)) * 100}
                  valueText={`ready ${formatGameDate(inProg.completeDay)}`}
                />
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Tree */}
      {([1, 2, 3, 4] as const).map((tier) => (
        <PanelSection key={tier} title={TIER_LABELS[tier]}>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {RESEARCH_TREE.filter((n) => n.tier === tier).map((node) => {
              const done = has(state, node.id);
              const unlocked = isUnlocked(state, node.id);
              const busy = !!inProg;
              return (
                <Card key={node.id} className={cn(!unlocked && !done && 'opacity-55', done && 'border-emerald-500/40')}>
                  <CardContent className="space-y-2 py-4">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold leading-tight">{node.name}</p>
                      {done ? (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                      ) : !unlocked ? (
                        <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground">{node.desc}</p>
                    <Badge variant="outline" className="whitespace-normal text-left text-[11px]">{node.effectText}</Badge>
                    {!done && (
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-xs tabular-nums text-muted-foreground">{money(node.cost)} · ~{researchDays(state, node.rDays)}d</span>
                        <Button
                          size="sm"
                          disabled={!unlocked || busy || state.player.cash < node.cost}
                          onClick={() => {
                            const err = startResearch(node.id);
                            if (err) toast({ title: 'Research unavailable', description: err, variant: 'destructive' });
                          }}
                        >
                          Start
                        </Button>
                      </div>
                    )}
                    {!done && !unlocked && (
                      <p className="text-[11px] text-muted-foreground">
                        Requires: {node.prereqs.map((p) => RESEARCH_TREE.find((n) => n.id === p)?.name ?? p).join(', ')}
                        {busy ? ' · lab busy' : ''}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </PanelSection>
      ))}

      {/* Team */}
      <PanelSection title="Team" desc={`Overhead ${money(state.team.overheadDaily)}/day. People are leverage — and fixed cost.`}>
        <Card>
          <CardContent className="space-y-3 py-4">
            {state.team.hires.map((h) => (
              <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 border-b pb-3 last:border-0 last:pb-0">
                <div>
                  <p className="text-sm font-medium">{h.name} <span className="ml-1 text-xs text-muted-foreground">({h.role})</span></p>
                  <p className="text-xs text-muted-foreground">{h.effectText} · {money(h.dailyCost)}/day</p>
                </div>
                <Button
                  size="sm"
                  variant={h.hired ? 'secondary' : 'outline'}
                  disabled={h.hired}
                  onClick={() => {
                    const err = hire(h.id);
                    if (err) toast({ title: 'Cannot hire', description: err, variant: 'destructive' });
                  }}
                >
                  {h.hired ? (
                    <><Users className="mr-1.5 h-3.5 w-3.5" /> On board</>
                  ) : (
                    `Hire — ${money(h.dailyCost * 30)}/mo`
                  )}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </PanelSection>
    </div>
  );
}
